import store from '../store/store';
import { addOfflineFile, removeFile, type OfflineFileData } from '../store/BakingAndCoolingSlice';
import type { FileDisplayData } from './BakingAndCoolingFileUpload';

// Convert file to base64
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Generate unique ID for offline files
function generateUniqueId(cycleNum: number): string {
  return `baking_cooling_${cycleNum}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Store file offline in Redux
export async function storeFileOffline(
  cycleNum: number, 
  file: File, 
  qualityTourId: string,
  productName?: string
): Promise<void> {
  const uniqueId = generateUniqueId(cycleNum);
  const previewUrl = URL.createObjectURL(file);
  
  try {
    const fileData = await fileToBase64(file);
    
    const offlineFileData: OfflineFileData = {
      id: uniqueId,
      cycleNum,
      productName: productName || undefined,
      fileData,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      timestamp: Date.now(),
      qualityTourId,
      previewUrl
    };
    
    store.dispatch(addOfflineFile(offlineFileData));
    console.log(`File stored offline for Cycle ${cycleNum}, Product ${productName || 'N/A'}:`, file.name);
  } catch (error) {
    console.error('Error converting file to base64:', error);
    throw error;
  }
}

// Get file display data for a cycle (safe when bakingAndCooling slice not yet rehydrated or missing)
export function getFileDisplayData(cycleNum: number, productName?: string): FileDisplayData[] {
  try {
    const state = store.getState();
    const allOfflineFiles = state.bakingAndCooling?.offlineFiles || [];
  
  // Filter files by cycle number (handle both number and string comparisons)
  const offlineFiles = allOfflineFiles.filter(file => {
    if (!file || typeof file !== 'object') return false;
    const fileCycleNum = typeof file.cycleNum === 'string' ? parseInt(file.cycleNum, 10) : file.cycleNum;
    const matchesCycle = fileCycleNum === cycleNum;
    const fileProduct = (file.productName || '').toString().toLowerCase();
    const matchesProduct = !productName || fileProduct === (productName || '').toLowerCase();
    return matchesCycle && matchesProduct;
  });
  
  console.log(`getFileDisplayData: Checking cycle ${cycleNum}${productName ? `, product ${productName}` : ''}`);
  console.log(`  Total files in Redux: ${allOfflineFiles.length}`);
  console.log(`  Files matching cycle ${cycleNum}:`, offlineFiles.map(f => ({ 
    id: f.id, 
    cycleNum: f.cycleNum, 
    fileName: f.fileName, 
    productName: f.productName,
    hasPreviewUrl: !!f.previewUrl 
  })));
  
  return offlineFiles.map(file => {
    // For SharePoint files (prefixed with 'sp_'), use SharePoint URL directly
    // For locally uploaded files, use object URL from base64
    const fileUrl = file.previewUrl || '';
    const isSharePointFile = (file.id || '').toString().startsWith('sp_');
    
    if (!fileUrl && isSharePointFile) {
      console.warn(`SharePoint file ${file.id} (${file.fileName}) has no previewUrl!`);
    }
    
    return {
      id: file.id,
      cycleNum: file.cycleNum,
      productName: file.productName,
      fileUrl: fileUrl,
      fileName: file.fileName,
      fileSize: file.fileSize,
      uploadTime: file.timestamp,
      isOffline: isSharePointFile ? false : true
    };
  });
  } catch (e) {
    console.warn('getFileDisplayData: Error reading offline files from Redux', e);
    return [];
  }
}

// Convert base64 back to File object
export function base64ToFile(base64: string, fileName: string, mimeType: string): File {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: mimeType });
}

// Upload offline file to SharePoint when coming back online
export async function uploadOfflineFile(
  offlineFile: OfflineFileData,
  accessToken: string
): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    // Convert base64 back to File
    const file = base64ToFile(offlineFile.fileData, offlineFile.fileName, offlineFile.fileType);
    
    // Import the upload function and create a temporary file input
    const { uploadFileToSharePointWithFile } = await import('./BakingAndCoolingFileUpload');
    
    // Upload to SharePoint with file parameter
    const result = await uploadFileToSharePointWithFile(
      offlineFile.cycleNum,
      offlineFile.qualityTourId,
      accessToken,
      file,
      offlineFile.productName
    );
    
    if (result.success) {
      // Remove from offline files
      store.dispatch(removeFile(offlineFile.id));
      console.log(`Offline file synced: ${offlineFile.fileName}`);
    }
    
    return result;
  } catch (error) {
    console.error('Error uploading offline file:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to upload offline file'
    };
  }
}

// Sync all offline files when coming back online
export async function syncOfflineFiles(accessToken: string): Promise<{ success: boolean; message: string }> {
  try {
    const state = store.getState();
    const offlineFiles = state.bakingAndCooling?.offlineFiles || [];
    
    if (offlineFiles.length === 0) {
      return { success: true, message: "No offline files to sync" };
    }

    store.dispatch({ type: 'bakingAndCooling/setSyncing', payload: true });
    
    const results: any[] = [];
    const errors: string[] = [];
    const uploadedFileIds: string[] = [];
    
    for (const offlineFile of offlineFiles) {
      try {
        const result = await uploadOfflineFile(offlineFile, accessToken);
        
        if (result.success) {
          uploadedFileIds.push(offlineFile.id);
          results.push(result);
        } else {
          errors.push(`Cycle ${offlineFile.cycleNum}: ${result.message}`);
        }
      } catch (error) {
        errors.push(`Cycle ${offlineFile.cycleNum}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    // Remove successfully uploaded files from Redux
    uploadedFileIds.forEach(fileId => {
      store.dispatch(removeFile(fileId));
    });
    
    store.dispatch({ type: 'bakingAndCooling/setSyncing', payload: false });
    
    if (errors.length > 0) {
      return {
        success: false,
        message: `Some files failed to sync: ${errors.join(', ')}`
      };
    }
    
    return {
      success: true,
      message: `Successfully synced ${results.length} offline files`
    };
    
  } catch (error) {
    store.dispatch({ type: 'bakingAndCooling/setSyncing', payload: false });
    console.error("Sync error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to sync offline files"
    };
  }
}
